import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { ChevronDown } from "lucide-react";

/*
  DER WEG DORTHIN — schlanker Zeitstrahl (übernommen aus der aufgelösten
  Sektion "pilot-berlin"; alles Übrige wird durch "Realisiert" abgedeckt).
*/

const TIMELINE = [
  { n: "01", t: { de: "Idee", en: "Idea" }, y: "2023" },
  { n: "02", t: { de: "Entwicklung", en: "Development" }, y: "2024" },
  { n: "03", t: { de: "Prototyp", en: "Prototype" }, y: "2025" },
  { n: "04", t: { de: "Pilotprojekt Berlin", en: "Pilot Berlin" }, y: "2026" },
  { n: "05", t: { de: "Europäische Städte", en: "European Cities" }, y: "2027+" },
];

export default function Weg() {
  const { lang } = useLang();
  const label = lang === "de" ? "Der Weg dorthin" : "The journey";

  return (
    <section
      id="weg"
      data-testid="section-weg"
      className="relative bg-[#F7F5F1] text-[#1F2328] py-32 md:py-44"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1 }}
          className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-[#B18B5E] mb-14 font-display"
        >
          <span className="h-px w-8 bg-[#B18B5E]" />
          {label}
        </motion.div>

        {/* Desktop */}
        <div className="hidden md:block relative">
          <div aria-hidden className="absolute top-6 left-0 right-0 h-px bg-[#1F2328]/15 z-0" />
          <div className="grid grid-cols-5 gap-6 relative z-10">
            {TIMELINE.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col items-start"
                data-testid={`weg-timeline-${i}`}
              >
                <div className={`h-3 w-3 rounded-full ${i === 3 ? "bg-[#B18B5E]" : "bg-[#1F2328]/25"} mt-[18px] mb-6 relative z-10`}>
                  {i === 3 && (
                    <span className="absolute inset-0 rounded-full bg-[#B18B5E] animate-ping opacity-40" />
                  )}
                </div>
                <div className="text-[10px] tracking-[0.35em] uppercase text-[#1F2328]/45 font-display mb-3">
                  {s.n} · {s.y}
                </div>
                <h3
                  className={`font-display font-light tracking-[-0.015em] leading-[1.1] ${i === 3 ? "text-[#1F2328]" : "text-[#1F2328]/70"}`}
                  style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.5rem)" }}
                >
                  {s.t[lang]}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-6">
          {TIMELINE.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="flex items-start gap-5"
            >
              <div className={`h-2.5 w-2.5 rounded-full mt-2 shrink-0 ${i === 3 ? "bg-[#B18B5E]" : "bg-[#1F2328]/25"}`} />
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#1F2328]/45 font-display mb-1">
                  {s.n} · {s.y}
                </div>
                <h3 className={`font-display font-light text-lg tracking-[-0.01em] ${i === 3 ? "text-[#1F2328]" : "text-[#1F2328]/70"}`}>
                  {s.t[lang]}
                </h3>
                {i < TIMELINE.length - 1 && (
                  <ChevronDown className="mt-3 h-4 w-4 text-[#1F2328]/25" strokeWidth={1.5} />
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
